import request from 'app/api/request';
import ServerErrorHandler from 'app/ErrorHandler';
import { useQueryClient, useMutation, useQuery, UseQueryResult, UseMutationResult } from 'react-query';
import {
  TagContext,
  CreateTagContextInput,
  UpdateTagContextInput,
  DeleteTagContextInput,
  FindOrCreateTagContextInput,
  ExistsResponse,
  SuccessResponse,
  UUID,
} from './types';
import { documentTaxonomyConfigQueryKeys } from './document-taxonomy-config';

export const tagContextQueryKeys = {
  tagContext: 'TAG_CONTEXT',
  tagContextExists: 'TAG_CONTEXT_EXISTS',
} as const;

export function useTagContext(tagContextId: UUID | undefined): UseQueryResult<TagContext | null> {
  return useQuery(
    [tagContextQueryKeys.tagContext, tagContextId],
    async () => {
      const res = await request.get(`/gaby/tag-contexts/${tagContextId}`).catch((err: { status: number }) => {
        if (err.status === 404) {
          return null;
        }
        throw err;
      });
      return (res?.data as TagContext) || null;
    },
    {
      enabled: !!tagContextId,
      onError: ServerErrorHandler,
    }
  );
}

export function useTagContextExists(tagContextId: UUID | undefined): UseQueryResult<ExistsResponse> {
  return useQuery(
    [tagContextQueryKeys.tagContextExists, tagContextId],
    async () => {
      const res = await request.get(`/gaby/tag-contexts/${tagContextId}/exists`).catch((err: { status: number }) => {
        if (err.status === 404) {
          return { data: { exists: false } };
        }
        throw err;
      });
      return (res?.data as ExistsResponse) || { exists: false };
    },
    {
      enabled: !!tagContextId,
      onError: ServerErrorHandler,
    }
  );
}

export function useFindOrCreateTagContext(): UseMutationResult<TagContext, unknown, FindOrCreateTagContextInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async (input: FindOrCreateTagContextInput) => {
      const res = await request.post('/gaby/tag-contexts/find-or-create', input);
      return res?.data as TagContext;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: () => {
        queryClient.invalidateQueries([documentTaxonomyConfigQueryKeys.configByDocument]);
      },
    }
  );
}

export function useCreateTagContext(): UseMutationResult<TagContext, unknown, CreateTagContextInput> {
  return useMutation(
    async (tagContext: CreateTagContextInput) => {
      const res = await request.post('/gaby/tag-contexts/', tagContext);
      return res?.data as TagContext;
    },
    {
      onError: ServerErrorHandler,
    }
  );
}

export function useUpdateTagContext(): UseMutationResult<SuccessResponse, unknown, UpdateTagContextInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ tagContextId, documentSuperId, ...tagContextData }: UpdateTagContextInput) => {
      const res = await request.put(`/gaby/tag-contexts/${tagContextId}?documentSuperId=${documentSuperId}`, tagContextData);
      return res?.data as SuccessResponse;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([tagContextQueryKeys.tagContext, variables.tagContextId]);
        queryClient.invalidateQueries([documentTaxonomyConfigQueryKeys.configByDocument]);
      },
    }
  );
}

export function useDeleteTagContext(): UseMutationResult<SuccessResponse, unknown, DeleteTagContextInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ tagContextId }: DeleteTagContextInput) => {
      const res = await request.del(`/gaby/tag-contexts/${tagContextId}`);
      return res?.data as SuccessResponse;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([tagContextQueryKeys.tagContext, variables.tagContextId]);
      },
    }
  );
}
